import { useMutation, useQueryClient } from "@tanstack/react-query"
import { type FormEvent, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { Modal } from "@/componentes/ui/Modal"

import { monto } from "./dinero"
import type { Movimiento } from "./tipos"

/** El mismo umbral que el servidor (`LARGO_MINIMO_MOTIVO`). */
const MOTIVO_MINIMO = 10

/**
 * Anular un movimiento: no se borra, se tacha y deja de contar en el saldo.
 *
 * ⚠ **Dice lo que arrastra ANTES de anular.** Si el movimiento pagó el abono de una
 * cuenta por pagar o cobró una por cobrar, el abono se anula con él y esa cuenta
 * vuelve a quedar abierta (`_cascada_de_anulacion`). Quien anula tiene que saberlo
 * antes, no descubrirlo cuando la deuda reaparece.
 *
 * ⚠ **El botón no se enciende sin diez caracteres de motivo**, en vez de dejar pulsar
 * y enseñar el 422. Un motivo de tres letras no es un motivo: es lo único que va a
 * explicar esta decisión dentro de un mes.
 */
export function DialogoAnular({
  movimiento,
  divisa,
  onCerrar,
}: {
  movimiento: Movimiento | null
  divisa: string
  onCerrar: () => void
}) {
  const clienteQuery = useQueryClient()
  const [motivo, setMotivo] = useState("")

  const anular = useMutation({
    meta: { exito: "Movimiento anulado" },
    mutationFn: () =>
      api.post<Movimiento>(`/api/v1/transacciones/${movimiento?.id}/anular`, {
        motivo: motivo.trim(),
      }),
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["movimientos"] })
      clienteQuery.invalidateQueries({ queryKey: ["resumen-cuenta"] })
      cerrar()
    },
  })

  function cerrar() {
    setMotivo("")
    anular.reset()
    onCerrar()
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault()
    if (motivo.trim().length >= MOTIVO_MINIMO) anular.mutate()
  }

  return (
    <Modal
      abierto={movimiento !== null}
      titulo="Anular este movimiento"
      descripcion="No se borra: queda en el extracto, tachado, y deja de contar en el saldo."
      onCerrar={cerrar}
      ancho="sm"
    >
      {movimiento && (
        <form onSubmit={enviar} className="grid gap-4">
          <p className="text-sm">
            <strong>{movimiento.concepto}</strong> ·{" "}
            {movimiento.tipo === "INGRESO" ? "entrada" : "salida"} de{" "}
            <span className="tabular">{monto(movimiento.monto)}</span> {divisa}.
          </p>
          <p
            className="rounded-[var(--radius-control)] border p-3 text-xs"
            style={{ borderColor: "var(--warning)", color: "var(--fg)" }}
          >
            Si este movimiento pagó una cuenta por pagar o cobró una por cobrar, ese abono se anula
            también y la cuenta vuelve a quedar abierta.
          </p>

          <div>
            <label htmlFor="a-motivo" className="mb-1 block text-xs font-medium uppercase">
              Motivo
            </label>
            <textarea
              id="a-motivo"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              maxLength={200}
              rows={3}
              placeholder="Se registró en la cuenta equivocada"
              className="w-full rounded-[var(--radius-control)] border px-2 py-1.5 text-sm"
              style={{
                borderColor: "var(--border)",
                backgroundColor: "var(--surface)",
                color: "var(--fg)",
              }}
            />
            <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
              Al menos {MOTIVO_MINIMO} caracteres.
            </p>
          </div>

          {anular.isError && (
            <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
              {anular.error instanceof ErrorApi ? anular.error.message : "No se pudo anular"}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Boton variante="fantasma" onClick={cerrar}>
              Cancelar
            </Boton>
            <Boton
              variante="peligro"
              type="submit"
              disabled={motivo.trim().length < MOTIVO_MINIMO || anular.isPending}
            >
              {anular.isPending ? "Anulando…" : "Anular"}
            </Boton>
          </div>
        </form>
      )}
    </Modal>
  )
}
